import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  fetchActiveRateCard, updateRateCard, fetchProposalTemplate, fetchProposalPackages,
  fetchAllPlans, patchProposalTemplate, fetchRenderedTemplate,
  reorderSections, updateSection, addSection, deleteSection,
  fetchSegments, createSegment, updateSegment, fetchTiers, createTier, updateTier, deleteTier,
  fetchFeatureMaster, fetchPlanFeatures,
} from "../services/rateCardService";

/**
 * The active rate card. Both the master screen and the proposal form read it —
 * the proposal form only to show recommended rates and limits; the prices
 * themselves always come from /proposals/calculate.
 */
export const useActiveRateCard = () =>
  useQuery({
    queryKey: ["rate-card", "active"],
    queryFn: fetchActiveRateCard,
    staleTime: 0,
    refetchOnMount: "always",
  });

/* No stale window on any of these: an edit to the rate card or the template has
   to show up the next time a screen opens it, or a rep quotes yesterday's
   prices. They are small calls; correctness beats saving one request. */
/** The editable config — the content editor's source. */
export const useProposalTemplate = () =>
  useQuery({
    queryKey: ["proposal-template"],
    queryFn: fetchProposalTemplate,
    staleTime: 0,
    refetchOnMount: "always",
  });

/**
 * A document ready to print. Keyed by doc and segment because the API resolves
 * both — switching either has to fetch, not reuse a neighbour's copy.
 */
export const useRenderedTemplate = (doc, segment) =>
  useQuery({
    queryKey: ["proposal-rendered", doc, segment || "none"],
    queryFn: () => fetchRenderedTemplate(doc, segment),
    enabled: Boolean(doc),
    staleTime: 0,
    refetchOnMount: "always",
  });

/* Every section write invalidates both the editable config and the rendered
   documents — one piece of content feeds both. */
const useSectionMutation = (mutationFn, successMessage) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (res) => {
      // The server's own wording wins; ours is only the fallback.
      const said = res?.message || successMessage;
      if (said) toast.success(said);
      queryClient.invalidateQueries({ queryKey: ["proposal-template"], refetchType: "all" });
      queryClient.invalidateQueries({ queryKey: ["proposal-rendered"], refetchType: "all" });
    },
    // The service layer has already turned 403 / 422 into a readable line.
    onError: (err) => toast.error(err?.message || "Something went wrong"),
  });
};

export const useReorderSections = () =>
  useSectionMutation(({ doc, order }) => reorderSections(doc, order), "Order saved");

export const useUpdateSection = () =>
  useSectionMutation(({ doc, key, patch }) => updateSection(doc, key, patch), "Section saved");

export const useAddSection = () =>
  useSectionMutation(({ doc, section, position }) => addSection(doc, section, position), "Section added");

export const useDeleteSection = () =>
  useSectionMutation(({ doc, key }) => deleteSection(doc, key), "Section deleted");

/* One cache entry, read two ways. Both hooks share a query key, so the packages
   call is made once however many of them a screen uses, and each `select`
   hands back only the part that caller needs. */
const packagesQuery = (segment, select) => ({
  queryKey: ["proposal-packages", segment],
  queryFn: () => fetchProposalPackages(segment),
  enabled: !!segment,
  staleTime: 0,
  refetchOnMount: "always",
  select,
});

export const useProposalPackages = (segment) =>
  useQuery(packagesQuery(segment, (body) => (Array.isArray(body?.data) ? body.data : [])));

/**
 * The settings the same response carries alongside the packages.
 *
 * These are the server's own defaults, so the form shows them rather than
 * stating a number of its own — a figure hardcoded here would go on claiming
 * "30 days" long after someone changed it on the server.
 */
export const useProposalDefaults = (segment) =>
  useQuery(packagesQuery(segment, (body) => ({
    paymentLinkExpiryHours: body?.default_payment_link_expiry_hours ?? null,
  })));

/** Saves one section of the document content at a time. */
export const usePatchProposalTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch) => patchProposalTemplate(patch),
    onSuccess: (res) => {
      toast.success(res?.message || "Content saved");
      queryClient.invalidateQueries({ queryKey: ["proposal-template"], refetchType: "all" });
      // The rendered documents print this same content.
      queryClient.invalidateQueries({ queryKey: ["proposal-rendered"], refetchType: "all" });
    },
    onError: (err) => toast.error(err?.message || "The content could not be saved"),
  });
};

/**
 * The plans a tier can be mapped to.
 *
 * `includeProposalOnly` pulls in the hidden band-plans as well — the Rate Card
 * needs them, and a tier already linked to one would otherwise show as having no
 * plan at all.
 */
export const useAllPlans = ({ includeProposalOnly = false, enabled = true } = {}) =>
  useQuery({
    queryKey: ["all-plans", includeProposalOnly],
    queryFn: () => fetchAllPlans({ includeProposalOnly }),
    enabled,
    staleTime: 0,
    refetchOnMount: "always",
  });

export const useUpdateRateCard = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name, config }) => updateRateCard(id, { name, config }),
    onSuccess: (res) => {
      toast.success(res?.message || "Rate card saved");
      queryClient.invalidateQueries({ queryKey: ["rate-card"], refetchType: "all" });
    },
    // The service layer has already turned 403 / 422 into a readable line.
    onError: (err) => toast.error(err?.message || "The rate card could not be saved"),
  });
};

/* ── Segments and tiers ──────────────────────────────────────────────────── */

export const useSegments = () =>
  useQuery({
    queryKey: ["proposal-segments"],
    queryFn: fetchSegments,
    staleTime: 0,
    refetchOnMount: "always",
  });

/** Keyed by segment: each tab holds a different set of rows. */
export const useTiers = (segment) =>
  useQuery({
    queryKey: ["rate-card-tiers", segment || "none"],
    queryFn: () => fetchTiers(segment),
    enabled: Boolean(segment),
    staleTime: 0,
    refetchOnMount: "always",
  });

export const useFeatureMaster = () =>
  useQuery({
    queryKey: ["feature-master"],
    queryFn: fetchFeatureMaster,
    // Features are added and renamed on their own screen; a five-minute-old copy
    // meant a new one simply was not on the card.
    staleTime: 0,
    refetchOnMount: "always",
  });

/**
 * Every tier write invalidates the packages and the quote alongside the rows:
 * a band whose price just changed must not keep quoting the old one.
 */
const useTierMutation = (mutationFn, success) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["rate-card-tiers"], refetchType: "all" });
      qc.invalidateQueries({ queryKey: ["proposal-packages"], refetchType: "all" });
      qc.invalidateQueries({ queryKey: ["rate-card", "active"], refetchType: "all" });
      /* The resolved feature list is derived from the row that just changed —
         left alone, reopening the tier showed the list it had before the save. */
      qc.invalidateQueries({ queryKey: ["tier-features"], refetchType: "all" });
      toast.success(res?.message || success);
    },
    onError: (err) => toast.error(err?.message || "That did not save"),
  });
};

export const useCreateTier = () => useTierMutation(createTier, "Tier added");
export const useUpdateTier = () =>
  useTierMutation(({ id, ...tier }) => updateTier(id, tier), "Tier updated");
export const useDeleteTier = () => useTierMutation(deleteTier, "Tier deleted");

export const useCreateSegment = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createSegment,
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["proposal-segments"] });
      toast.success(res?.message || "Segment added");
    },
    onError: (err) => toast.error(err?.message || "The segment could not be created"),
  });
};

/**
 * What a tier will give: its own features, or its linked plan's. Idle until one
 * of the two ids is known, so the editor never asks for nothing.
 */
export const usePlanFeatures = ({ planId, rateCardId, segment }) =>
  useQuery({
    queryKey: ["tier-features", planId || 0, rateCardId || 0, segment || ""],
    queryFn: () => fetchPlanFeatures({ planId, rateCardId, segment }),
    enabled: Boolean(planId || rateCardId),
    staleTime: 0,
    /* A plan's features are edited on the plan screen, which does not go through
       this cache and cannot invalidate it. Asking every time this form opens is
       the only way the tier shows the plan as it is now. */
    refetchOnMount: "always",
  });

/**
 * A taper edit re-prices every open quote, so the packages and any cached quote
 * go stale alongside the segment itself.
 */
export const useUpdateSegment = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }) => updateSegment(id, patch),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["proposal-segments"] });
      qc.invalidateQueries({ queryKey: ["proposal-packages"] });
      toast.success(res?.message || "Rates saved");
    },
    onError: (err) => toast.error(err?.message || "The segment could not be saved"),
  });
};
